import { useEffect, useMemo, useState } from "react";
import { BellRing, CheckCheck, ExternalLink, Paperclip } from "lucide-react";
import { useNavigate } from "react-router-dom";

import api, { API } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";


function formatDate(value) {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
}


export default function ResidentNotificationCenter() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], categories: [], unread_count: 0 });
  const [filter, setFilter] = useState("ALL");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const response = await api.get("/resident-notifications");
      setData(response.data);
    } catch {
      setData({ items: [], categories: [], unread_count: 0 });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const items = useMemo(() => {
    if (filter === "UNREAD") return data.items.filter((item) => !item.read);
    if (filter === "ALL") return data.items;
    return data.items.filter((item) => item.category === filter);
  }, [data.items, filter]);

  const openItem = async (item) => {
    setSelected(item);
    setDetail(null);
    if (!item.read) {
      await api.post(`/resident-notifications/${item.item_id}/read`).catch(() => {});
      await load();
    }
    if (item.source === "NOTICE") {
      const response = await api
        .get(`/resident-communications/notices/${item.record_id}`)
        .catch(() => ({ data: null }));
      setDetail(response.data);
    }
  };

  const markAll = async () => {
    await api.post("/resident-notifications/mark-all-read").catch(() => {});
    await load();
  };

  return (
    <section
      className="border border-slate-200 bg-white p-5 shadow-sm"
      data-testid="resident-notification-center"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
            Updates
          </p>
          <h2 className="mt-1 font-display text-xl font-bold text-slate-950">
            Notification centre
          </h2>
        </div>
        <Button
          data-testid="resident-notifications-mark-all"
          disabled={!data.unread_count}
          onClick={markAll}
          size="sm"
          variant="outline"
        >
          <CheckCheck />
          Mark all read
        </Button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2" data-testid="resident-notification-filters">
        {["ALL", "UNREAD", ...data.categories].map((category) => (
          <button
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              filter === category
                ? "border-sky-700 bg-sky-700 text-white"
                : "border-slate-200 bg-white text-slate-600 hover:border-sky-300"
            }`}
            data-testid={`resident-notification-filter-${category.toLowerCase().replaceAll(" ", "-")}`}
            key={category}
            onClick={() => setFilter(category)}
            type="button"
          >
            {category === "ALL" ? "All" : category === "UNREAD" ? "Unread" : category}
          </button>
        ))}
      </div>
      <div
        className="mt-4 max-h-[430px] space-y-2 overflow-y-auto pr-1"
        data-testid="resident-notification-scroll-list"
      >
        {items.length ? (
          items.map((item) => (
            <button
              className={`w-full border p-3 text-left transition-[border-color,background-color] ${
                item.read
                  ? "border-slate-200 bg-white hover:border-sky-200"
                  : "border-sky-200 bg-sky-50 hover:border-sky-400"
              }`}
              data-testid={`resident-notification-${item.item_id.replace(":", "-")}`}
              key={item.item_id}
              onClick={() => openItem(item)}
              type="button"
            >
              <div className="flex items-start gap-3">
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                    item.read ? "bg-transparent" : "bg-sky-600"
                  }`}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-3">
                    <span className="truncate text-xs font-semibold text-sky-800">
                      {item.category}
                    </span>
                    <span className="shrink-0 text-xs text-slate-500">
                      {formatDate(item.created_at)}
                    </span>
                  </span>
                  <span className="mt-1 block text-sm font-semibold text-slate-900">
                    {item.title}
                  </span>
                  <span className="mt-1 line-clamp-2 block text-xs leading-5 text-slate-600">
                    {item.message}
                  </span>
                </span>
              </div>
            </button>
          ))
        ) : (
          <div className="border border-dashed border-slate-200 p-6 text-center" data-testid="resident-notification-empty">
            <BellRing className="mx-auto h-5 w-5 text-slate-400" />
            <p className="mt-2 text-sm text-slate-600">No updates match this view.</p>
          </div>
        )}
      </div>
      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="resident-notification-detail">
          <DialogHeader>
            <DialogTitle className="pr-8 font-display text-2xl">{selected?.title}</DialogTitle>
            <DialogDescription className="sr-only">
              Notification details and related actions.
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">
            {selected?.category}
          </p>
          <p className="whitespace-pre-wrap text-sm leading-7 text-slate-700">
            {detail?.notice?.body || selected?.message}
          </p>
          {detail?.attachments?.length > 0 && (
            <div className="border-t border-slate-200 pt-4" data-testid="resident-notification-attachments">
              <p className="text-sm font-semibold text-slate-900">Attachments</p>
              <div className="mt-3 space-y-2">
                {detail.attachments.map((attachment) => (
                  <a
                    className="flex items-center justify-between gap-3 border border-slate-200 p-3 text-sm font-medium text-sky-800 hover:border-sky-300"
                    data-testid={`resident-notification-attachment-${attachment.doc_id}`}
                    href={`${API}/resident-communications/notices/${selected.record_id}/attachments/${attachment.doc_id}/download`}
                    key={attachment.doc_id}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <span className="flex min-w-0 items-center gap-2 truncate">
                      <Paperclip className="h-4 w-4 shrink-0" />
                      {attachment.title}
                    </span>
                    <ExternalLink className="h-4 w-4 shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}
          {selected?.link && (
            <Button
              data-testid="resident-notification-related-record"
              onClick={() => {
                setSelected(null);
                navigate(selected.link);
              }}
              variant="outline"
            >
              Open related record
              <ExternalLink />
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}